// components/MyChart.js
import React, { useState, useEffect } from 'react';
import NormalButton from './NormalButton';

const Pagination = ({ total, itemsPerPage, currentPage, setCurrentPage }) => {
  const [goToPage, setGoToPage] = useState('');

  // Calculate pagination values
  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = endIndex - startIndex;

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Pagination handlers
  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToSpecificPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleGoToPage = () => {
    const page = parseInt(goToPage);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setGoToPage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  return (
    <>
      {/* Pagination */}
      <div className="flex items-center justify-center flex-wrap gap-x-20 gap-y-4 text-sm text-[#1B2128] font-normal">
        {/* Page buttons */}
        <div className="inline-flex items-center justify-center space-x-1">
          {/* Previous button */}
          <button
            onClick={goToPrevPage}
            disabled={currentPage === 1}
            className="w-[32px] h-[32px] sm:w-[38px] sm:h-[38px] flex items-center justify-center hover:bg-gray-100 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              className="shrink-0 size-3.5"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 18-6-6 6-6"></path>
            </svg>
          </button>

          {/* Page numbers */}
          {getPageNumbers().map((page, idx) => (
            <button
              key={idx}
              onClick={() => typeof page === 'number' && goToSpecificPage(page)}
              className={`w-[32px] h-[32px] sm:w-[38px] sm:h-[38px] flex items-center justify-center rounded cursor-pointer ${
                page === currentPage
                  ? 'bg-gray-200'
                  : typeof page === 'number'
                  ? 'hover:bg-gray-100'
                  : 'cursor-default'
              }`}
              disabled={typeof page !== 'number'}
            >
              {page}
            </button>
          ))}

          {/* Next button */}
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className="w-[32px] h-[32px] sm:w-[38px] sm:h-[38px] flex items-center justify-center hover:bg-gray-100 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              className="shrink-0 size-3.5"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m9 18 6-6-6-6"></path>
            </svg>
          </button>
        </div>

        {/* Go to page input */}
        <div className="h-8 sm:h-[38px] flex items-center justify-center space-x-2">
          <span className="cursor-default">Go to</span>
          <input
            type="number"
            min="1"
            max={totalPages}
            value={goToPage}
            onChange={(e) => setGoToPage(e.target.value)}
            onKeyDown={handleKeyPress}
            className="w-16 h-8 sm:h-9 px-3 border border-gray-300 rounded-[8px] text-center"
            placeholder={currentPage.toString()}
          />
          <span>page</span>
          <NormalButton
            customClass="bg-[#092F7B] text-white"
            text="Go"
            onClick={handleGoToPage}
          />
        </div>
      </div>

      {/* Debug info */}
      <div className="text-xs text-gray-500 text-center pb-4">
        Mostrando {currentItems} de {total} transacciones (Página {currentPage}{' '}
        de {totalPages})
      </div>
    </>
  );
};

export default Pagination;
